<template>
    <v-card class="scrollable-container elevation-5">
        <v-card-title class="px-4">
            <div>{{ pageTitle }}</div>
        </v-card-title>
        <v-card-text class="px-4 pb-2 flex-grow-1">
            <div>
                <v-form ref="form" v-model="isFormValid">
                    <v-container fluid pa-0>
                        <v-row no-gutters>
                            <v-col class="px-2" md="3" sm="6">
                                <v-text-field
                                    v-model="email"
                                    label="Email *"
                                    :rules="emailRules"
                                    required
                                    :disabled="!(isMsoUser && isEditMode)"
                                    @input="onFormChange"
                                ></v-text-field>
                            </v-col>
                            <v-col
                                v-show="isMsoUser"
                                class="px-2"
                                md="3"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="username"
                                    label="Username *"
                                    :rules="usernameRules"
                                    required
                                    disabled
                                    @input="onFormChange"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row no-gutters>
                            <v-col class="px-2" md="3" sm="6">
                                <v-text-field
                                    v-model="firstName"
                                    label="First name *"
                                    :rules="firstNameRules"
                                    required
                                    :disabled="!isEditMode"
                                    @input="onFormChange"
                                ></v-text-field>
                            </v-col>
                            <v-col class="px-2" md="3" sm="6">
                                <v-text-field
                                    v-model="lastName"
                                    label="Last name *"
                                    :rules="lastNameRules"
                                    required
                                    :disabled="!isEditMode"
                                    @input="onFormChange"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </div>
            <div>
                <component
                    :is="scopesDetailsComponent"
                    :allow-add="false"
                    :allow-edit="false"
                    :user-id="userInfo.id"
                    :scopes-data="
                        transformUserScopesData(
                            userInfo.userScopes || [],
                            userInfo.userScopes || [],
                            true
                        )
                    "
                    :grantable-scopes-data="userInfo.userScopes || []"
                    :assignable-scopes-data="[]"
                >
                </component>
            </div>
        </v-card-text>
        <v-card-actions class="justify-end px-4">
            <v-btn
                v-show="!isEditMode && !isMsoUser"
                small
                color="primary"
                :disabled="canEditUser"
                @click="onChangePasswordClick"
                >change password</v-btn
            >
            <v-btn
                v-show="!isEditMode"
                small
                color="primary"
                :disabled="canEditUser"
                @click="onEditClick"
                >edit</v-btn
            >
            <v-btn v-show="isEditMode" outlined small @click="onCancelClick"
                >cancel</v-btn
            >
            <v-btn
                v-show="isEditMode"
                small
                color="primary"
                :disabled="!(isFormDirty && isFormValid)"
                @click="onSaveClick"
                >save</v-btn
            >
        </v-card-actions>
        <confirmation-dialog ref="confirmation"></confirmation-dialog>
    </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import ScopeMasterDetailView from './components/ScopeMasterDetailView';
import SingleScopeView from './components/SingleScopeView';
import userScopeMixin from '@/mixins/userScopeMixin';

export default {
    name: 'UserProfilePage',
    components: {
        'confirmation-dialog': ConfirmationDialog,
        'scope-master-detail-view': ScopeMasterDetailView,
        'single-scope-view': SingleScopeView
    },
    mixins: [userScopeMixin],
    data() {
        return {
            isFormDirty: false,
            isFormValid: false,
            isDataSaving: false,
            isEditMode: false,
            username: '',
            email: '',
            firstName: '',
            lastName: '',
            usernameRules: [
                v => !!v || 'Username is required',
                v =>
                    (v && v.length <= 64) ||
                    'Username must be less than 64 characters',
                v =>
                    (v && this.validUsername(v)) ||
                    'Username must be the same as email'
            ],
            emailRules: [
                v => !!v || 'Email is required',
                v => (v && this.validEmail(v)) || 'Must be valid Email format'
            ],
            firstNameRules: [
                v => !!v || 'First name is required',
                v =>
                    (v && v.length <= 64) ||
                    'First name must be less than 64 characters'
            ],
            lastNameRules: [
                v => !!v || 'Last name is required',
                v =>
                    (v && v.length <= 64) ||
                    'Last name must be less than 64 characters'
            ],
            formErrorMessage: ''
        };
    },
    computed: {
        ...mapState('authUser', ['userInfo']),
        pageTitle() {
            if (this.userInfo) {
                return `${this.isEditMode ? 'Edit ' : ''}${
                    this.userInfo.firstName
                } ${this.userInfo.lastName}'s Profile`;
            }
            return 'User Profile';
        },
        isMsoUser() {
            if (this.userInfo) {
                return this.userInfo.xocAuth === true;
            }
            return false;
        },
        canEditUser() {
            return this.$can('edit', 'User') && this.userId > 0;
        },
        isSingleScopeMode() {
            return (
                this.userInfo &&
                this.userInfo.userScopes &&
                this.userInfo.userScopes.length === 1 &&
                !this.isScopeWildcardMatch(this.userInfo.userScopes[0])
            );
        },
        scopesDetailsComponent() {
            if (this.isSingleScopeMode) {
                return 'single-scope-view';
            }
            return 'scope-master-detail-view';
        }
    },
    created() {
        this.initUserForm();
    },
    methods: {
        ...mapActions('authUser', ['updateUserProfile']),
        // ===================== Events =======================
        onFormChange() {
            this.isFormDirty = true;
        },
        onChangePasswordClick() {
            this.$router.push({ name: 'changePasswordPage' });
        },
        onEditClick() {
            this.isEditMode = true;
        },
        onCancelClick() {
            if (this.isEditMode && this.isFormDirty) {
                this.showSaveConfirmation();
            }
            this.isEditMode = false;
        },
        onSaveClick() {
            if (this.isFormDirty && this.$refs.form.validate()) {
                this.saveThisUser();
            }
            this.isEditMode = false;
        },
        // ====================== Other =======================
        validEmail(email) {
            var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(email);
        },
        validUsername(username) {
            if (!this.isMsoUser && this.email) {
                return username === this.email;
            }
            return true;
        },
        initUserForm() {
            if (this.userInfo && this.userInfo.id > 0) {
                this.username = this.userInfo.username;
                this.email = this.userInfo.email;
                this.firstName = this.userInfo.firstName;
                this.lastName = this.userInfo.lastName;
            }
        },
        resetViewState() {
            this.isEditMode = false;
            this.isDataSaving = false;
            this.isFormDirty = false;
            this.$refs.form.resetValidation();
        },
        async showSaveConfirmation() {
            const message = 'Are you sure you want to close without saving?';
            const subMessage = 'It look like you have some unsaved changes';
            const title = 'Edit User Profile';
            const options = {
                confirmActionText: 'Save the changes',
                cancelActionText: 'Ignore the changes',
                showIcon: true
            };
            const res = await this.$refs.confirmation.open(
                title,
                message,
                subMessage,
                options
            );
            if (res) {
                this.saveThisUser();
                this.resetViewState();
                this.initUserForm();
            } else {
                this.initUserForm();
                this.resetViewState();
            }
        },
        async saveThisUser() {
            // will call th api to save user data
            const userObject = {};
            if (this.email !== this.userInfo.email) {
                userObject.email = this.email;
            }
            if (this.firstName !== this.userInfo.firstName) {
                userObject.firstName = this.firstName;
            }
            if (this.lastName !== this.userInfo.lastName) {
                userObject.lastName = this.lastName;
            }
            if (Object.keys(userObject).length) {
                await this.updateUserProfile(userObject);
                // need to refresh the current user data
                this.resetViewState();
                this.initUserForm();
            }
        }
    }
};
</script>

<style></style>
